import { computed } from '@ember/object';
import { readOnly, equal } from '@ember/object/computed';
import DatePair from '@square/dashboard-data/models/squareup/bills/date-pair';
import Destination from '@square/dashboard-data/models/squareup/beemo/api/v2/models/destination';
import { ProtoBaseModel, attr } from '@square/dashboard-data/utils/ember-model-stubs';
import SettlementDetails from '@square/dashboard-data/models/squareup/bills/card-tender/settlement-details';
import MoneyProto from '@square/dashboard-data/models/squareup/common/money-proto';
import ISO8601 from '@square/dashboard-data/models/squareup/bills/iso8601-date';
import CreatorDetails from '@square/dashboard-data/models/squareup/beemo/api/v2/models/creator-details';
import ClientDetails from '@square/dashboard-data/models/squareup/beemo/api/v2/models/client-details';
import Tender, { Type } from '@square/dashboard-data/models/squareup/beemo/api/v2/models/tender';
import BillTenderReportingAmounts from '@square/dashboard-data/models/squareup/bills/bill-tender-reporting-amounts';
import MoneyNegatorProxy from '@square/dashboard-money/utils/money-negator-proxy';
import Enum from '@square/dashboard-utilities/utils/enum';
import { isIn } from '@square/dashboard-utilities/macros';
import refundType from './refund-type';
const State = new Enum('UNKNOWN', 'NEW', 'PENDING_AUTHORIZATION', 'PENDING', 'CANCELED', 'SUCCESS', 'FAILED');
export default ProtoBaseModel.extend({
  state: attr(State),
  linkedBillServerToken: attr(String),
  linkedTenderServerToken: attr(String),
  linkedTender: attr(Tender),
  destination: attr(Destination),
  refundMoney: attr(MoneyProto),
  creatorDetails: attr(CreatorDetails),
  clientDetails: attr(ClientDetails),
  reason: attr(String),
  requestedAt: attr(DatePair),
  refundedAt: attr(ISO8601),
  refundedReportingAmounts: attr(BillTenderReportingAmounts),
  settlementDetails: attr(SettlementDetails),
  readOnlyLinkedTenderType: attr(String),
  refundType: attr(refundType),
  // Aliases for naming consistency with models/squareup/beemo/api/v2/models/refund
  createdAt: readOnly('requestedAt'),
  paymentToken: readOnly('linkedTender.paymentToken'),
  refundedMoney: readOnly('refundMoney.negated'),
  // Refund V3 amounts have signs with respect to the sale, need to negate to display refunded amount
  refundedProcessingFeeMoney: readOnly('refundedReportingAmounts.processingFeeMoney.negated'),
  refundedProcessingFeeAmounts: computed('refundedReportingAmounts.processingFeeAmounts', function () {
    return MoneyNegatorProxy.create({
      toNegate: this.get('refundedReportingAmounts.processingFeeAmounts')
    });
  }),
  refundedProcessingFeeAmountDetails: readOnly('refundedReportingAmounts.processingFeeAmounts'),
  positiveRefundedNetTotalMoney: readOnly('refundedNetTotalMoney.negated'),
  tenderType: readOnly('linkedTender.type'),
  isCreditCard: equal('tenderType', Type.CREDIT_CARD),
  isCash: equal('tenderType', Type.CASH),
  isPending: isIn('state', [State.PENDING_AUTHORIZATION, State.PENDING]),
  isRejected: isIn('state', [State.CANCELLED, State.FAILED]),
  hasProcessingFees: readOnly('refundedProcessingFeeMoney.amount'),
  netRefundedProcessingFeeMoney: readOnly('refundedProcessingFeeMoney'),
  refundedNetTotalMoney: computed('refundedMoney', 'refundedProcessingFeeMoney', function () {
    const refundedMoney = this.refundedMoney;
    const refundedProcessingFeeMoney = this.refundedProcessingFeeMoney;
    return refundedMoney && refundedProcessingFeeMoney ? refundedMoney.add(refundedProcessingFeeMoney) : refundedMoney;
  }),
  receiptNumber: computed('paymentToken', function () {
    const paymentToken = this.paymentToken;
    return paymentToken ? paymentToken.slice(0, 4) : paymentToken;
  })
});